import { useState } from "preact/hooks";
import type { Ctx } from "../app";
import { api, must, sb } from "../lib/supabase";
import {
  Badge, Card, ErrorBox, Loading, Modal, daysUntil, fmtDate, formData, go, toast, useAction, useLoad,
} from "../lib/ui";
import { CREDENTIAL_KINDS, type Payer, type Provider } from "../lib/types";
import { MANUAL_CHECKS, STATE_LOOKUP_LINKS } from "../../../src/shared/validators";
import { DocumentList, UploadDocument } from "./documents";
import { Readiness } from "./insights";

const canWrite = (ctx: Ctx) => ctx.role !== "viewer";

// ================================================================== list
export function ProvidersPage({ ctx }: { ctx: Ctx }) {
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const { data, error, reload } = useLoad(async () => {
    const [p, r] = await Promise.all([
      sb().from("providers").select("*").eq("org_id", ctx.orgId).order("last_name"),
      sb().from("v_provider_readiness").select("provider_id, readiness_pct, failing_checks, expiring_30d, last_checked_at").eq("org_id", ctx.orgId),
    ]);
    return { list: must(p) as Provider[], ready: new Map((must(r) as any[]).map((x) => [x.provider_id, x])) };
  }, [ctx.orgId]);

  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  const shown = data.list.filter((p) => `${p.first_name} ${p.last_name} ${p.email} ${p.npi ?? ""}`.toLowerCase().includes(q.toLowerCase()));

  const runAll = async () => {
    const targets = data.list.filter((p) => p.status !== "inactive");
    if (!confirm(`Run all automatic checks for ${targets.length} BCBAs? This takes a few seconds each.`)) return;
    let fails = 0;
    for (const [i, p] of targets.entries()) {
      setProgress(`Checking ${i + 1} of ${targets.length}: ${p.first_name} ${p.last_name}…`);
      try {
        const r = await api<{ results: Array<{ result: string }> }>(`/api/app/providers/${p.id}/validate`, {});
        fails += r.results.filter((x) => x.result === "fail").length;
      } catch (e) { toast(`${p.first_name} ${p.last_name}: ${(e as Error).message}`, true); }
    }
    setProgress(null); reload();
    toast(`Done: ${targets.length} BCBAs checked${fails ? `, ${fails} failing check(s) to review` : ", nothing failing"}.`, fails > 0);
  };

  const notInvited = data.list.filter((p) => p.status !== "inactive" && !p.intake_link_sent_at);
  const inviteAll = async () => {
    if (!confirm(`Email the intake link to ${notInvited.length} BCBA(s) who haven't been invited yet?`)) return;
    let sent = 0;
    for (const [i, p] of notInvited.entries()) {
      setProgress(`Inviting ${i + 1} of ${notInvited.length}: ${p.first_name} ${p.last_name}…`);
      try { await api(`/api/app/providers/${p.id}/intake-link`, {}); sent++; }
      catch (e) { toast(`${p.first_name} ${p.last_name}: ${(e as Error).message}`, true); }
    }
    setProgress(null); reload();
    toast(`${sent} of ${notInvited.length} intake link(s) sent. Reminders go out automatically on days 2, 5 and 10.`, sent < notInvited.length);
  };

  return (
    <Card title={`BCBAs (${data.list.length})`} actions={<>
      <input placeholder="Search name, email, NPI" value={q} onInput={(e) => setQ(e.currentTarget.value)} />
      {canWrite(ctx) && notInvited.length > 0 && <button class="ghost" disabled={!!progress} onClick={inviteAll}>Invite {notInvited.length} not yet invited</button>}
      {canWrite(ctx) && <button class="ghost" disabled={!!progress} onClick={runAll}>Run checks for all</button>}
      {canWrite(ctx) && <button onClick={() => setAdding(true)}>Add BCBA</button>}
    </>}>
      {progress && <p class="banner">{progress}</p>}
      <table>
        <thead><tr><th>Name</th><th>States</th><th>NPI</th><th>Readiness</th><th>Alerts</th><th>Last checked</th><th>Status</th></tr></thead>
        <tbody>{shown.map((p) => {
          const r = data.ready.get(p.id);
          return (
            <tr class="click" onClick={() => go(`providers/${p.id}`)}>
              <td><a href={`#/providers/${p.id}`}>{p.last_name}, {p.first_name}</a><div class="muted small">{p.email}</div></td>
              <td>{p.practice_states.join(", ") || "—"}</td><td>{p.npi ?? "—"}</td>
              <td><Readiness pct={r?.readiness_pct ?? null} /></td>
              <td>{r?.failing_checks ? <span class="badge bad">{r.failing_checks} failing</span> : null}
                {r?.expiring_30d ? <span class="badge warn">{r.expiring_30d} expiring</span> : null}
                {!r?.failing_checks && !r?.expiring_30d ? <span class="muted">—</span> : null}</td>
              <td class="small">{r?.last_checked_at ? fmtDate(r.last_checked_at) : <span class="muted">never</span>}</td>
              <td><Badge value={p.status} /></td>
            </tr>);
        })}</tbody>
      </table>
      {adding && <ProviderForm ctx={ctx} onClose={() => setAdding(false)} onSaved={(id) => { setAdding(false); reload(); go(`providers/${id}`); }} />}
    </Card>
  );
}

/** One-page summary of everything staff type into payer applications. Printable. */
export function ProviderPacket({ ctx, id }: { ctx: Ctx; id: string }) {
  const { data, error } = useLoad(async () => {
    const [p, creds, vals, apps] = await Promise.all([
      sb().from("providers").select("*").eq("id", id).single(),
      sb().from("provider_credentials").select("*").eq("provider_id", id).order("kind").order("expires_on", { ascending: false }),
      sb().from("validation_checks").select("source, result, summary, checked_at").eq("provider_id", id).order("checked_at", { ascending: false }),
      sb().from("applications").select("status, effective_on, submitted_on, payers(name)").eq("provider_id", id),
    ]);
    return { p: must(p) as Provider, creds: must(creds) as any[], vals: must(vals) as any[], apps: must(apps) as any[] };
  }, [id]);
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  const { p } = data;
  const latest = new Map<string, any>();
  for (const v of data.vals) if (!latest.has(v.source)) latest.set(v.source, v);
  const newest = new Map<string, any>();
  for (const c of data.creds) if (!newest.has(c.kind)) newest.set(c.kind, c);
  const label = Object.fromEntries(CREDENTIAL_KINDS);
  return (
    <>
      <p class="no-print"><a href={`#/providers/${id}`}>← Back</a> · <button onClick={() => print()}>Print / save as PDF</button></p>
      <div class="packet">
        <h1>Credentialing summary — {p.first_name} {p.last_name}, BCBA</h1>
        <p class="small">{ctx.orgName} · generated {new Date().toLocaleString()}</p>
        <table><tbody>
          <tr><th>Individual NPI</th><td>{p.npi ?? "—"}</td><th>CAQH ProView ID</th><td>{p.caqh_id ?? "—"}</td></tr>
          <tr><th>Email</th><td>{p.email}</td><th>Phone</th><td>{p.phone ?? "—"}</td></tr>
          <tr><th>Practices in</th><td colSpan={3}>{p.practice_states.join(", ") || "—"}</td></tr>
        </tbody></table>
        <h3>Licenses, certification and insurance</h3>
        <table>
          <thead><tr><th>Item</th><th>State</th><th>Number</th><th>Issued</th><th>Expires</th><th>Verified</th></tr></thead>
          <tbody>{[...newest.values()].map((c) => (
            <tr><td>{label[c.kind] ?? c.kind}{c.kind === "malpractice" && c.coverage_per_claim ? ` ($${c.coverage_per_claim.toLocaleString()} / $${c.coverage_aggregate?.toLocaleString() ?? "?"})` : ""}</td>
              <td>{c.state ?? ""}</td><td>{c.number ?? ""}</td><td>{fmtDate(c.issued_on)}</td><td>{fmtDate(c.expires_on)}</td>
              <td class="small">{c.verified_at ? `${fmtDate(c.verified_at)} — ${c.verification_source ?? ""}` : "not yet verified"}</td></tr>))}</tbody>
        </table>
        <h3>Latest verification results</h3>
        <table><tbody>{[...latest.values()].map((v) => <tr><td>{v.source.replaceAll("_", " ")}</td><td><strong>{v.result}</strong></td><td class="small">{v.summary}</td><td class="small">{fmtDate(v.checked_at)}</td></tr>)}</tbody></table>
        <h3>Payers</h3>
        <table><tbody>{data.apps.map((a) => <tr><td>{a.payers?.name}</td><td>{a.status.replaceAll("_", " ")}</td><td>{a.effective_on ? `effective ${fmtDate(a.effective_on)}` : a.submitted_on ? `submitted ${fmtDate(a.submitted_on)}` : ""}</td></tr>)}</tbody></table>
        <p class="small">No Social Security number or date of birth is stored in this system; the BCBA provides those directly in CAQH and payer portals.</p>
      </div>
    </>
  );
}

function ProviderForm({ ctx, provider, onClose, onSaved }: { ctx: Ctx; provider?: Provider; onClose: () => void; onSaved: (id: string) => void }) {
  const { busy, run } = useAction();
  return (
    <Modal title={provider ? "Edit BCBA" : "Add BCBA"} onClose={onClose}>
      <form class="grid2" onSubmit={(e) => {
        const f = formData(e);
        const states = (["NY", "NJ"] as const).filter((s) => (e.currentTarget as HTMLFormElement)[`st_${s}`].checked);
        const row = { first_name: f.first_name, last_name: f.last_name, email: f.email?.toLowerCase(), phone: f.phone,
          npi: f.npi, caqh_id: f.caqh_id, practice_states: states, status: f.status ?? "onboarding", notes: f.notes };
        run(async () => {
          const res = provider
            ? must(await sb().from("providers").update(row).eq("id", provider.id).select("id").single())
            : must(await sb().from("providers").insert({ ...row, org_id: ctx.orgId }).select("id").single());
          onSaved((res as { id: string }).id);
        }, "Saved");
      }}>
        <label>First name<input name="first_name" required value={provider?.first_name} /></label>
        <label>Last name<input name="last_name" required value={provider?.last_name} /></label>
        <label>Email<input name="email" type="email" required value={provider?.email} /></label>
        <label>Phone<input name="phone" value={provider?.phone ?? ""} /></label>
        <label>NPI (individual)<input name="npi" inputMode="numeric" pattern="\d{10}" value={provider?.npi ?? ""} /></label>
        <label>CAQH ProView ID<input name="caqh_id" inputMode="numeric" pattern="\d{5,10}" value={provider?.caqh_id ?? ""} /></label>
        <fieldset><legend>Practices in</legend>
          {(["NY", "NJ"] as const).map((s) => <label class="inline"><input type="checkbox" name={`st_${s}`} checked={provider?.practice_states.includes(s)} /> {s}</label>)}
        </fieldset>
        <label>Status<select name="status" value={provider?.status ?? "onboarding"}>
          <option value="onboarding">onboarding</option><option value="active">active</option><option value="inactive">inactive</option>
        </select></label>
        <label class="span2">Internal notes (no SSNs or client info)<textarea name="notes" rows={2}>{provider?.notes ?? ""}</textarea></label>
        <div class="span2 row-end"><button type="button" class="ghost" onClick={onClose}>Cancel</button><button disabled={busy}>Save</button></div>
      </form>
    </Modal>
  );
}

// ================================================================== detail
export function ProviderDetail({ ctx, id }: { ctx: Ctx; id: string }) {
  const [editing, setEditing] = useState(false);
  const [docKey, setDocKey] = useState(0);
  const { busy, run } = useAction();
  const { data, error, reload } = useLoad(async () => {
    const [p, creds, apps, vals, payers] = await Promise.all([
      sb().from("providers").select("*").eq("id", id).single(),
      sb().from("provider_credentials").select("*").eq("provider_id", id).order("kind").order("expires_on", { ascending: false }),
      sb().from("applications").select("*, payers(name, slug), legal_entities(legal_name)").eq("provider_id", id),
      sb().from("validation_checks").select("*").eq("provider_id", id).order("checked_at", { ascending: false }),
      sb().from("payers").select("id, name, states, priority_tier").eq("active", true).order("priority_tier").order("name"),
    ]);
    return { p: must(p) as Provider, creds: must(creds) as any[], apps: must(apps) as any[], vals: must(vals) as any[], payers: must(payers) as Payer[] };
  }, [id]);

  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  const { p } = data;

  return (
    <>
      <p><a href="#/providers">← All BCBAs</a></p>
      <Card title={`${p.first_name} ${p.last_name}`} actions={canWrite(ctx) && <>
        <a class="button ghost" href={`#/providers/${p.id}/packet`}>Credentialing packet</a>
        <button class="ghost" onClick={() => setEditing(true)}>Edit</button>
        <button disabled={busy} onClick={() => run(() => api(`/api/app/providers/${p.id}/intake-link`, {}).then(reload),
          `Intake link emailed to ${p.email}`)}>{p.intake_link_sent_at ? "Re-send intake link" : "Send intake link"}</button>
      </>}>
        <dl class="facts">
          <dt>Email</dt><dd>{p.email}</dd><dt>Phone</dt><dd>{p.phone ?? "—"}</dd>
          <dt>NPI</dt><dd>{p.npi ?? "—"}</dd><dt>CAQH ID</dt><dd>{p.caqh_id ?? "—"}</dd>
          <dt>Practices in</dt><dd>{p.practice_states.join(", ") || "—"}</dd><dt>Status</dt><dd><Badge value={p.status} /></dd>
          <dt>Intake link</dt><dd>{p.intake_link_sent_at ? `sent ${fmtDate(p.intake_link_sent_at)}` : "not sent"}{p.user_id ? " · portal account active" : ""}</dd>
        </dl>
        {p.notes && <p class="muted">{p.notes}</p>}
      </Card>

      <Credentials ctx={ctx} provider={p} creds={data.creds} reload={reload} />
      <Card title="Documents" actions={canWrite(ctx) && <UploadDocument orgId={p.org_id} providerId={p.id} creds={data.creds} onDone={() => setDocKey((k) => k + 1)} />}>
        <DocumentList providerId={p.id} staff={canWrite(ctx)} isAdmin={ctx.role === "owner" || ctx.role === "admin"} refreshKey={docKey} />
      </Card>
      <PayerChecklists ctx={ctx} provider={p} apps={data.apps} payers={data.payers} reload={reload} />
      <Validations ctx={ctx} provider={p} vals={data.vals} creds={data.creds} reload={reload} />
      {editing && <ProviderForm ctx={ctx} provider={p} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); reload(); }} />}
    </>
  );
}

function Credentials({ ctx, provider, creds, reload }: { ctx: Ctx; provider: Provider; creds: any[]; reload: () => void }) {
  const [adding, setAdding] = useState(false);
  const { busy, run } = useAction();
  const label = Object.fromEntries(CREDENTIAL_KINDS);
  return (
    <Card title="Credentials & expirations" actions={canWrite(ctx) && <button onClick={() => setAdding(true)}>Add credential</button>}>
      {creds.length === 0 ? <p class="muted">None recorded yet.</p> : (
        <table>
          <thead><tr><th>Item</th><th>State</th><th>Number</th><th>Issued / attested</th><th>Expires</th><th>Verified</th></tr></thead>
          <tbody>{creds.map((c) => {
            const d = c.expires_on ? daysUntil(c.expires_on) : null;
            return (
              <tr>
                <td>{label[c.kind] ?? c.kind}{c.kind === "malpractice" && c.coverage_per_claim ? <div class="muted small">${c.coverage_per_claim.toLocaleString()} / ${c.coverage_aggregate?.toLocaleString() ?? "?"}</div> : null}</td>
                <td>{c.state ?? "—"}</td><td>{c.number ?? "—"}</td><td>{fmtDate(c.issued_on)}</td>
                <td>{c.expires_on ? <><span class={`badge ${d! < 0 ? "bad" : d! <= 60 ? "warn" : "good"}`}>{d! < 0 ? "expired" : `${d}d`}</span> {fmtDate(c.expires_on)}</> : "—"}</td>
                <td>{c.verified_at ? <>{fmtDate(c.verified_at)}<div class="muted small">{c.verification_source}</div></> :
                  canWrite(ctx) ? <button class="small ghost" disabled={busy} onClick={() => {
                    const source = prompt("Where did you verify it? (e.g. NYSED online verification)");
                    if (source) run(async () => { must(await sb().from("provider_credentials").update({ verified_at: new Date().toISOString(), verified_by: ctx.userId, verification_source: source }).eq("id", c.id)); reload(); }, "Marked verified");
                  }}>Mark verified</button> : "—"}</td>
              </tr>);
          })}</tbody>
        </table>
      )}
      {adding && (
        <Modal title="Add credential" onClose={() => setAdding(false)}>
          <form class="grid2" onSubmit={(e) => {
            const f = formData(e);
            run(async () => {
              must(await sb().from("provider_credentials").insert({
                org_id: provider.org_id, provider_id: provider.id, kind: f.kind, state: f.state, number: f.number,
                issued_on: f.issued_on, expires_on: f.expires_on,
                coverage_per_claim: f.coverage_per_claim ? Number(f.coverage_per_claim) : null,
                coverage_aggregate: f.coverage_aggregate ? Number(f.coverage_aggregate) : null, notes: f.notes,
              }));
              setAdding(false); reload();
            }, "Credential added");
          }}>
            <label>Type<select name="kind" required>{CREDENTIAL_KINDS.map(([k, l]) => <option value={k}>{l}</option>)}</select></label>
            <label>State<input name="state" maxLength={2} placeholder="NY" /></label>
            <label>Number<input name="number" /></label>
            <label>Issued / attested on<input name="issued_on" type="date" /></label>
            <label>Expires on<input name="expires_on" type="date" /></label>
            <label>Malpractice per claim ($)<input name="coverage_per_claim" type="number" min="0" /></label>
            <label>Malpractice aggregate ($)<input name="coverage_aggregate" type="number" min="0" /></label>
            <label class="span2">Notes<input name="notes" /></label>
            <p class="span2 muted small">CAQH attestation must be done by the BCBA personally in CAQH ProView. Record it here only after they confirm.</p>
            <div class="span2 row-end"><button disabled={busy}>Save</button></div>
          </form>
        </Modal>
      )}
    </Card>
  );
}

function PayerChecklists({ ctx, provider, apps, payers, reload }: { ctx: Ctx; provider: Provider; apps: any[]; payers: Payer[]; reload: () => void }) {
  const [picking, setPicking] = useState(false);
  const { busy, run } = useAction();
  const { data: checklist } = useLoad(async () =>
    must(await sb().from("v_application_checklist").select("*").eq("provider_id", provider.id)) as any[], [provider.id, apps.length]);
  const relevant = payers.filter((py) => py.states.some((s) => provider.practice_states.includes(s)));
  const have = new Set(apps.map((a) => a.payer_id));

  return (
    <Card title="Payers & checklists" actions={canWrite(ctx) && <button onClick={() => setPicking(true)}>Add payers</button>}>
      {apps.length === 0 && <p class="muted">No payer applications yet. Use “Add payers” to start a checklist for each payer.</p>}
      {apps.map((a) => {
        const items = (checklist ?? []).filter((c) => c.application_id === a.id);
        const missing = items.filter((i) => i.status !== "met" && i.status !== "unverified" && i.required === "yes").length;
        const unverified = items.filter((i) => i.status === "unverified").length;
        return (
          <details class="checklist">
            <summary>
              <strong>{a.payers.name}</strong> <Badge value={a.status} />
              {missing ? <span class="badge bad">{missing} missing</span> : null}
              {unverified ? <span class="badge warn">{unverified} to verify</span> : null}
              {!missing && !unverified && items.length ? <span class="badge good">requirements met</span> : null}
              <a class="small" href={`#/applications/${a.id}`} onClick={(e) => e.stopPropagation()}>open application →</a>
            </summary>
            <table>
              <thead><tr><th>Requirement</th><th>Required?</th><th>Status</th><th>Source</th></tr></thead>
              <tbody>{items.map((i) => (
                <tr>
                  <td>{i.label}{i.note && <div class="muted small">{i.note}</div>}</td>
                  <td>{i.required === "unknown" ? <span class="badge warn">verify with payer</span> : i.required}</td>
                  <td><Badge value={i.status} /></td>
                  <td class="small">{i.source_urls?.slice(0, 2).map((u: string, n: number) => <a href={u} target="_blank" rel="noopener noreferrer">src{n + 1} </a>)}<span class="muted">({i.confidence})</span></td>
                </tr>))}</tbody>
            </table>
          </details>
        );
      })}
      {picking && (
        <Modal title="Start applications" onClose={() => setPicking(false)}>
          <form onSubmit={(e) => {
            e.preventDefault();
            const ids = [...new FormData(e.currentTarget).getAll("payer")].map(String);
            run(async () => {
              if (ids.length) must(await sb().from("applications").insert(ids.map((payer_id) => ({ org_id: ctx.orgId, provider_id: provider.id, payer_id }))));
              setPicking(false); reload();
            }, `${ids.length} application(s) created`);
          }}>
            <p class="muted small">Showing payers in {provider.practice_states.join(" & ") || "no states — set practice states first"}.</p>
            {relevant.length === 0 && <p class="error">The payer list hasn't loaded yet. It loads automatically within the hour after a deploy; try again shortly.</p>}
            <div class="checks">{relevant.map((py) => (
              <label class="inline"><input type="checkbox" name="payer" value={py.id} disabled={have.has(py.id)} /> {py.name} <span class="muted small">T{py.priority_tier}</span></label>
            ))}</div>
            <div class="row-end"><button disabled={busy || relevant.length === 0}>Create</button></div>
          </form>
        </Modal>
      )}
    </Card>
  );
}

/** What to type into each manual lookup site, so staff can copy it in one tap. */
function searchValues(source: string, p: Provider, creds: any[]): Array<[string, string]> {
  const num = (kind: string) => creds.find((c) => c.kind === kind && c.number)?.number as string | undefined;
  const name: Array<[string, string]> = [["Last name", p.last_name], ["First name", p.first_name]];
  if (source === "bacb_registry") return [...name, ...(num("bacb_certification") ? [["Cert #", num("bacb_certification")!] as [string, string]] : [])];
  if (source === "nysed_license") return [["Profession", "Licensed Behavior Analyst"], ...(num("ny_lba") ? [["License #", num("ny_lba")!] as [string, string]] : name)];
  if (source === "nj_dca_license") return [["Profession", "Applied Behavior Analyst"], ...(num("nj_lba") ? [["License #", num("nj_lba")!] as [string, string]] : name)];
  return name;
}
function SearchHint({ values }: { values: Array<[string, string]> }) {
  return (
    <div class="small">{values.map(([k, v]) => (
      <button type="button" class="chip" title="Copy" onClick={() => navigator.clipboard.writeText(v).then(() => toast(`Copied ${k}`))}>{k}: <strong>{v}</strong> ⧉</button>
    ))}</div>
  );
}

const AUTO_LABEL: Record<string, string> = {
  nppes: "NPPES (NPI registry)", oig_leie: "OIG federal exclusion list", ny_omig: "NY OMIG Medicaid exclusion list",
  ny_medicaid_enrolled: "NY Medicaid enrollment", nysed_license: "NYSED license (NY)", nj_dca_license: "NJ license (Consumer Affairs)",
};

function Validations({ ctx, provider, vals, creds, reload }: { ctx: Ctx; provider: Provider; vals: any[]; creds: any[]; reload: () => void }) {
  const { busy, run } = useAction();
  const latest = new Map<string, any>();
  for (const v of vals) if (!latest.has(v.source)) latest.set(v.source, v);
  const ny = provider.practice_states.includes("NY");
  const nj = provider.practice_states.includes("NJ");
  const auto = ["nppes", "oig_leie", ...(ny ? ["nysed_license", "ny_omig", "ny_medicaid_enrolled"] : []), ...(nj ? ["nj_dca_license"] : [])];
  const nyNumber = creds.find((c) => c.kind === "ny_lba" && c.number)?.number as string | undefined;
  const lookup: Record<string, string> = { nysed_license: STATE_LOOKUP_LINKS.nysed_license(nyNumber), nj_dca_license: STATE_LOOKUP_LINKS.nj_dca_license() };
  const bacb = MANUAL_CHECKS[0];
  const bv = latest.get("bacb_registry");
  const bacbCred = creds.find((c) => c.kind === "bacb_certification");

  return (
    <Card title="Verification" actions={canWrite(ctx) && <button disabled={busy} onClick={() => run(async () => {
      const r = await api<{ results: Array<{ source: string; result: string }> }>(`/api/app/providers/${provider.id}/validate`, {});
      reload(); toast(`Checks done: ${r.results.map((x) => `${AUTO_LABEL[x.source] ?? x.source}: ${x.result}`).join(" · ")}`);
    })}>Run automatic checks</button>}>
      <table>
        <thead><tr><th>Source</th><th>Result</th><th>Summary</th><th>Checked</th></tr></thead>
        <tbody>
          {auto.map((s) => {
            const v = latest.get(s);
            return (
              <tr>
                <td>{AUTO_LABEL[s]} <span class="muted small">auto</span>
                  {lookup[s] && <div class="small"><a href={lookup[s]} target="_blank" rel="noopener noreferrer">open official lookup ↗</a></div>}</td>
                <td><Badge value={v?.result ?? "not run"} /></td><td>{v?.summary ?? "—"}</td><td>{v ? fmtDate(v.checked_at) : "—"}</td>
              </tr>);
          })}
          <tr>
            <td><a href={bacb.url} target="_blank" rel="noopener noreferrer">{bacb.label} ↗</a>
              <SearchHint values={searchValues("bacb_registry", provider, creds)} />
              <div class="muted small">Manual: {bacb.why}</div></td>
            <td><Badge value={bv?.result ?? "not checked"} /></td>
            <td>{bv?.summary ?? "—"}</td>
            <td>{bv && <div>{fmtDate(bv.checked_at)}</div>}
              {canWrite(ctx) && <form class="bacb-form" onSubmit={(e) => {
                const f = formData(e);
                const result = (e as SubmitEvent).submitter?.getAttribute("value") ?? "pass";
                run(async () => {
                  const summary = result === "pass"
                    ? `BACB Registry: BCBA ${f.cert} active, expires ${fmtDate(f.expires)} (checked by hand).`
                    : `BACB Registry checked by hand: ${result}${f.cert ? ` (cert ${f.cert})` : ""}.`;
                  must(await sb().from("validation_checks").insert({ org_id: provider.org_id, provider_id: provider.id, source: "bacb_registry",
                    mode: "manual", result, summary, details: { certification_number: f.cert, expiration: f.expires } }));
                  if (result === "pass" && f.cert && f.expires) {
                    const verified = { number: f.cert, expires_on: f.expires, verified_at: new Date().toISOString(), verified_by: ctx.userId,
                      verification_source: "BACB Certificant Registry (checked by hand)" };
                    if (bacbCred) must(await sb().from("provider_credentials").update(verified).eq("id", bacbCred.id));
                    else must(await sb().from("provider_credentials").insert({ org_id: provider.org_id, provider_id: provider.id, kind: "bacb_certification", ...verified }));
                  }
                  reload();
                }, "BACB result recorded");
              }}>
                <input name="cert" placeholder="Cert # (1-24-76027)" value={bacbCred?.number ?? ""} />
                <label class="small">Expires<input name="expires" type="date" value={bacbCred?.expires_on ?? ""} /></label>
                <span class="btns">
                  <button class="small" value="pass" disabled={busy}>Active ✓</button>
                  <button class="small ghost" value="fail" disabled={busy}>Not active</button>
                  <button class="small ghost" value="review" disabled={busy}>Unsure</button>
                </span>
              </form>}
            </td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
}
